import { useEffect, useMemo, useState } from 'react'
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'

interface Company {
  id: string
  company_name: string
  company_code: string
}

type ReportKey =
  | ''
  | 'weekly_shift_report'
  | 'weekly_repair_summary'
  | 'repair_notifications'
  | 'incident_notifications'
  | 'time_off_request_notifications'

interface ReportOption {
  key: ReportKey
  label: string
  hint: string
}

const REPORT_OPTIONS: ReportOption[] = [
  { key: '', label: 'Generic Test', hint: 'Confirms outbound email is connected at all — no template.' },
  { key: 'time_off_request_notifications', label: 'Time Off Request', hint: 'What admins get when an employee submits a PTO/time-off request.' },
  { key: 'repair_notifications', label: 'Repair Notification', hint: 'What admins get when a new equipment repair is logged.' },
  { key: 'incident_notifications', label: 'Incident Report', hint: 'What admins get when an incident is reported.' },
  { key: 'weekly_repair_summary', label: 'Weekly Repair Summary', hint: 'The scheduled Sunday-night repair digest.' },
  { key: 'weekly_shift_report', label: 'Weekly Shift Report', hint: 'The scheduled Sunday-night shift/hours digest.' },
]

interface SendResult {
  ok: boolean
  message: string
  company?: string
}

export default function EmailNotificationTest() {
  const { user } = useAuth()
  const [companies, setCompanies] = useState<Company[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(true)
  const [companyCode, setCompanyCode] = useState('')
  const [email, setEmail] = useState('')
  const [reportKey, setReportKey] = useState<ReportKey>('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<SendResult | null>(null)
  const [history, setHistory] = useState<SendResult[]>([])

  useEffect(() => {
    api.get('/super-admin/companies')
      .then(res => setCompanies(res.data?.companies || []))
      .catch(() => setCompanies([]))
      .finally(() => setLoadingCompanies(false))
  }, [])

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email)
  }, [user?.email])

  const selectedOption = useMemo(() => REPORT_OPTIONS.find(r => r.key === reportKey), [reportKey])

  const sendTest = async () => {
    if (!companyCode || !email) return
    setSending(true)
    setResult(null)
    try {
      const res = await api.post('/super-admin/email-notifications/test-send', {
        companyCode,
        email,
        reportKey: reportKey || undefined,
      })
      const entry: SendResult = {
        ok: true,
        message: `${selectedOption?.label || 'Generic Test'} sent to ${email}`,
        company: res.data?.company,
      }
      setResult(entry)
      setHistory(h => [entry, ...h].slice(0, 8))
    } catch (error: any) {
      const entry: SendResult = {
        ok: false,
        message: error.response?.data?.message || 'Failed to send test email',
      }
      setResult(entry)
      setHistory(h => [entry, ...h].slice(0, 8))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-emerald-600 flex items-center justify-center shadow-sm">
          <EnvelopeIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-slate-900 leading-tight">Email Notification Test</h1>
          <p className="text-xs text-slate-500">Send any tenant's real notification emails to your own inbox to verify delivery</p>
        </div>
      </div>

      <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800">
        <InformationCircleIcon className="w-5 h-5 shrink-0 mt-0.5" />
        <p>
          This tests the actual per-tenant notification flows (PTO requests, repairs, incidents, weekly digests) — not the raw
          platform templates on the <span className="font-semibold">Email Templates</span> page. Each option sends the same
          content a real recipient would get, generated from sample data, through the same code path.
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <span className="inline-flex items-center gap-1.5"><BuildingOfficeIcon className="w-4 h-4 text-slate-400" /> Company</span>
            </label>
            <select
              value={companyCode}
              onChange={e => setCompanyCode(e.target.value)}
              disabled={loadingCompanies}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white disabled:opacity-50"
            >
              <option value="">{loadingCompanies ? 'Loading companies…' : 'Select a company…'}</option>
              {companies.map(c => (
                <option key={c.id} value={c.company_code}>{c.company_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <span className="inline-flex items-center gap-1.5"><EnvelopeIcon className="w-4 h-4 text-slate-400" /> Send to</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2.5">What to test</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {REPORT_OPTIONS.map(opt => (
              <button
                key={opt.key || 'generic'}
                type="button"
                onClick={() => setReportKey(opt.key)}
                className={`text-left p-3 rounded-xl border transition-all ${
                  reportKey === opt.key
                    ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-100'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <p className={`text-sm font-semibold ${reportKey === opt.key ? 'text-blue-900' : 'text-slate-800'}`}>{opt.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{opt.hint}</p>
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={sendTest}
          disabled={sending || !companyCode || !email}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50"
        >
          {sending ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PaperAirplaneIcon className="w-4 h-4" />}
          {sending ? 'Sending…' : 'Send Test Email'}
        </button>

        {result && (
          <div className={`flex items-start gap-2.5 p-3.5 rounded-xl text-sm ${
            result.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {result.ok ? <CheckCircleIcon className="w-5 h-5 shrink-0" /> : <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />}
            <span>{result.message}{result.company ? ` (${result.company})` : ''}</span>
          </div>
        )}
      </div>

      {/* Recent sends this session */}
      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">This session</h3>
          <ul className="space-y-2">
            {history.map((h, i) => (
              <li key={i} className="flex items-center gap-2.5 text-sm">
                {h.ok ? <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" /> : <ExclamationTriangleIcon className="w-4 h-4 text-red-500 shrink-0" />}
                <span className={h.ok ? 'text-slate-700' : 'text-red-700'}>{h.message}{h.company ? ` (${h.company})` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
